import { useCallback, useEffect, useRef, useState } from "react";

import { checkScrollLimit, getCardWidth, getVisibleCards } from "./utlis";

export const useCarousel = ({ cards, className }: { cards: unknown[]; className: string }) => {
  const [carouselState, setcarouselState] = useState(() => ({
    scrollStep: 1,
    numberOfCards: cards.length,
    cardsWidth: 0,
    cardsInView: 0,
    width: 0,
    scroll: 0,
    current: 1,
  }));

  const containerRef = useRef<HTMLDivElement>(null);

  const moveCarousel = useCallback((direction: "prev" | "next") => {
    setcarouselState((prevState) => {
      if (!containerRef.current) {
        return prevState;
      }

      const { scroll, cardsWidth, scrollStep } = prevState;
      let newScroll = scroll;

      if (direction === "prev") {
        newScroll -= cardsWidth * scrollStep;
      }

      if (direction === "next") {
        newScroll += cardsWidth * scrollStep;
      }

      containerRef.current.scrollLeft = newScroll;

      return {
        ...prevState,
        scroll: newScroll,
      };
    });
  }, []);

  const handlePrev = useCallback(() => moveCarousel("prev"), [moveCarousel]);
  const handleNext = useCallback(() => moveCarousel("next"), [moveCarousel]);
  const handleCheckScrollLimit = useCallback(() => {
    return checkScrollLimit(
      carouselState.cardsWidth,
      carouselState.scroll,
      carouselState.numberOfCards,
      carouselState.cardsInView,
    );
  }, [carouselState]);

  useEffect(() => {
    const cardsWidth = getCardWidth(containerRef, className);

    const cardsInView = containerRef.current ? getVisibleCards(containerRef.current, className) : 0;
    const numberOfCards = cards.length;

    setcarouselState((prev) => ({
      ...prev,
      numberOfCards,
      cardsWidth,
      cardsInView,
      scroll: 0,
    }));
  }, [cards.length, className]);

  useEffect(() => {
    containerRef.current?.addEventListener("scroll", (e) => {
      const { currentTarget } = e as Event & {
        currentTarget: {
          scrollWidth: number;
          clientWidth: number;
          scrollLeft: number;
        };
      };
      if (currentTarget) {
        const totalWidth = currentTarget.scrollWidth;
        const visibleWidth = currentTarget.clientWidth;
        const scrollAmount = currentTarget.scrollLeft;

        const scrollPercent = (scrollAmount / (totalWidth - visibleWidth)) * 100;

        const percentItem = 100 / cards.length;

        const step = Math.floor(scrollPercent / percentItem + 1);
        setcarouselState((prev) => ({
          ...prev,
          current: step >= cards.length ? cards.length : step,
        }));
      }
    });
  }, [cards.length, containerRef]);

  return {
    prev: handlePrev,
    next: handleNext,
    carouselState,
    containerRef,
    checkScrollLimit: handleCheckScrollLimit,
  };
};
